import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/hooks/use-mobile'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'
import { useIdTokenAndTenantId } from '@/hooks/useIdTokenAndTenantId'
import {
  projectDetailsOptions,
  projectDocumentsOptions,
} from '@/data/queryOptions'
import { useSuspenseQuery } from '@tanstack/react-query'
import { CategoryTag, type DocumentData } from '@/data/entities'
import { useMemo } from 'react'
import { DocumentLineCard } from '@/components/dashboard/documents-page/DocumentLineCard'

export const Route = createFileRoute(
  '/_authenticated/_dashboard/operator/projects/$projectId',
)({
  component: RouteComponent,
})

function RouteComponent() {
  const isMobile = useIsMobile()
  const { projectId } = useParams({
    from: '/_authenticated/_dashboard/operator/projects/$projectId',
  })
  const { idToken, TENANT_ID } = useIdTokenAndTenantId()
  const { data: project } = useSuspenseQuery({
    ...projectDetailsOptions(idToken!, TENANT_ID!, projectId),
  })

  const { data: projectDocuments } = useSuspenseQuery({
    ...projectDocumentsOptions({
      token: idToken!,
      tenantId: TENANT_ID!,
      projectId,
    }),
  })

  const categories = useMemo(() => Object.values(CategoryTag), [])
  const categoryDocuments = useMemo(() => {
    const cats: Record<string, DocumentData[] | undefined> = {}
    if (projectDocuments.length === 0) {
      return cats
    }
    let remainingDocuments = [...projectDocuments]
    for (const category of categories) {
      cats[category] = remainingDocuments.filter(
        (document) => document.metadata?.category === category,
      )
      remainingDocuments = remainingDocuments.filter(
        (document) => document.metadata?.category !== category,
      )
    }
    cats[CategoryTag.Others] = cats[CategoryTag.Others] || []
    cats[CategoryTag.Others].push(...remainingDocuments)
    return cats
  }, [categories, projectDocuments])

  return (
    <div className="flex h-screen w-full flex-col gap-4 overflow-y-hidden">
      {/* Desktop navigation */}
      {!isMobile && (
        <nav className="bg-background flex h-15 w-full items-center justify-between border-b px-4">
          <Button variant="ghost" asChild>
            <Link to="/documents">
              <ArrowLeftIcon /> Back
            </Link>
          </Button>
        </nav>
      )}
      <div className="flex-1 overflow-y-auto px-4">
        {/* workspace header */}
        <div className="pb-4">
          <h2 className="text-2xl font-semibold">{project.name}</h2>
          <p className="text-muted-foreground pl-4 text-sm">
            {project.description}
          </p>
        </div>

        {/* categories */}
        <div className="mb-8">
          <h3 className="text-sm font-bold">{CategoryTag.GetStarted}</h3>
          <p className="text-copy-secondary pb-4 text-sm">
            Basic setup instructions to help you power on, prepare, and safely
            begin using the machine.
          </p>
          <div className="flex flex-col gap-4">
            {categoryDocuments[CategoryTag.GetStarted]?.map((document) => (
              <DocumentLineCard key={document.id} document={document} />
            ))}
          </div>
        </div>
        <div className="mb-8">
          <h3 className="text-sm font-bold">{CategoryTag.Changeovers}</h3>
          <p className="text-copy-secondary pb-4 text-sm">
            Detailed procedures for changing over to a new product or process.
          </p>
          <div className="flex flex-col gap-4">
            {categoryDocuments[CategoryTag.Changeovers]?.map((document) => (
              <DocumentLineCard key={document.id} document={document} />
            ))}
          </div>
        </div>
        <div className="mb-8">
          <h3 className="text-sm font-bold">{CategoryTag.CommonIssues}</h3>
          <p className="text-copy-secondary pb-4 text-sm">
            Common issues and solutions to help you troubleshoot and fix
            problems.
          </p>
          <div className="flex flex-col gap-4">
            {categoryDocuments[CategoryTag.CommonIssues]?.map((document) => (
              <DocumentLineCard key={document.id} document={document} />
            ))}
          </div>
        </div>
        <div className="mb-8">
          <h3 className="text-sm font-bold">{CategoryTag.Others}</h3>
          <p className="text-copy-secondary pb-4 text-sm">
            Other documents that don't fit into the other categories.
          </p>
          <div className="flex flex-col gap-4">
            {categoryDocuments[CategoryTag.Others]?.map((document) => (
              <DocumentLineCard key={document.id} document={document} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
